import { Box, TextField, IconButton, TableCell, TableRow } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState, useEffect } from 'react';
import { useBooking } from '../../../../../context/bookingContext';

export default function SpecialLineItem({ item, index, onDelete, onUpdate }) {
  const [description, setDescription] = useState(item?.description || '');
  const [count, setCount] = useState(item?.count || 1);
  const [price, setPrice] = useState(item?.price || 0);
  const [displayPrice, setDisplayPrice] = useState(
    item?.price ? item.price.toFixed(2).replace('.', ',') : ''
  );
  const [displayCount, setDisplayCount] = useState(item?.count ? item.count.toString() : '');

  const { priceOverride } = useBooking();

  useEffect(() => {
    if (item) {
      setDescription(item.description || '');
      setCount(item.count || 1);
      setPrice(item.price || 0);
      setDisplayPrice(item.price ? item.price.toFixed(2).replace('.', ',') : '');
      setDisplayCount(item.count ? item.count.toString() : '');
    }
  }, [item]);

  const handleDescriptionChange = e => {
    const newDescription = e.target.value;
    setDescription(newDescription);
    onUpdate(index, { ...item, description: newDescription });
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  const handleCountChange = e => {
    const value = e.target.value;
    setDisplayCount(value);
  };

  const handleCountBlur = () => {
    const newCount = parseInt(displayCount) || 0;
    setCount(newCount);
    setDisplayCount(newCount > 0 ? newCount.toString() : '');
    onUpdate(index, { ...item, count: newCount });
  };

  const handlePriceChange = e => {
    const value = e.target.value;
    setDisplayPrice(value);
  };

  const handlePriceBlur = () => {
    const newPrice = displayPrice === '' ? 0 : parseFloat(displayPrice.replace(',', '.')) || 0;
    setPrice(newPrice);
    setDisplayPrice(newPrice > 0 ? newPrice.toFixed(2).replace('.', ',') : '');
    onUpdate(index, { ...item, price: newPrice });
  };

  const formatPrice = price => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  const isIncomplete = !description.trim() || count <= 0 || price <= 0;

  if (!priceOverride) {
    return null;
  }

  return (
    <TableRow
      sx={{
        backgroundColor: isIncomplete ? 'rgba(255, 0, 0, 0.1)' : 'inherit',
        '&:hover': {
          backgroundColor: isIncomplete ? 'rgba(255, 0, 0, 0.15)' : 'rgba(0, 0, 0, 0.04)',
        },
      }}
    >
      <TableCell
        align='left'
        sx={{ position: 'relative', verticalAlign: 'top', paddingTop: 2, paddingBottom: 2 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minHeight: '56px' }}>
          <TextField
            value={description}
            onChange={handleDescriptionChange}
            onKeyDown={handleKeyDown}
            placeholder='Beschreibung eingeben...'
            size='small'
            fullWidth
            error={!description.trim()}
            helperText={''}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: 'success.main',
                },
              },
              '& .MuiFormHelperText-root': {
                margin: 0,
                marginTop: 0.5,
              },
            }}
          />
          <IconButton
            size='small'
            onClick={() => onDelete(index)}
            color='error'
            sx={{
              flexShrink: 0,
              marginTop: 0.5,
              alignSelf: 'flex-start',
            }}
          >
            <DeleteIcon fontSize='small' />
          </IconButton>
        </Box>
      </TableCell>
      <TableCell align='right' sx={{ verticalAlign: 'top', paddingTop: 2, paddingBottom: 2 }}>
        <TextField
          value={displayPrice}
          onChange={handlePriceChange}
          onBlur={handlePriceBlur}
          onKeyDown={handleKeyDown}
          placeholder='0,00'
          size='small'
          type='text'
          inputProps={{
            style: { textAlign: 'right' },
          }}
          error={price <= 0}
          helperText={''}
          sx={{
            width: '120px',
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: 'success.main',
              },
            },
            '& .MuiFormHelperText-root': {
              margin: 0,
              marginTop: 0.5,
            },
          }}
        />
      </TableCell>
      <TableCell align='right' sx={{ verticalAlign: 'top', paddingTop: 2, paddingBottom: 2 }}>
        <TextField
          value={displayCount}
          onChange={handleCountChange}
          onBlur={handleCountBlur}
          onKeyDown={handleKeyDown}
          placeholder='1'
          size='small'
          type='text'
          inputProps={{
            style: { textAlign: 'right' },
          }}
          error={count <= 0}
          helperText={count <= 0 ? 'positive Anzahl erforderlich' : ''}
          sx={{
            width: '80px',
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: 'success.main',
              },
            },
            '& .MuiFormHelperText-root': {
              margin: 0,
              marginTop: 0.5,
            },
          }}
        />
      </TableCell>
      <TableCell align='right' sx={{ verticalAlign: 'top', paddingTop: 2, paddingBottom: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            minHeight: '56px',
            paddingTop: 1,
            paddingBottom: 1,
          }}
        >
          {formatPrice(price * count)}
        </Box>
      </TableCell>
    </TableRow>
  );
}
